import { Collapse } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { FieldValues, useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetDeliveryOfficeListQuery } from "../../../../api/services/mdmOffice/mdmOfficeApis";
import { useGetProgramClass } from "../../../../api/services/programTemplate/programTemplateApis";
import { ReactComponent as CollapseIcon } from "../../../../assets/images/collapse-icon.svg";
import { ReactComponent as ExpandIcon } from "../../../../assets/images/expand-icon.svg";
import {
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiRadioGroup,
  MuiTypography,
  OptionsDataType,
} from "../../../../components";
import {
  LANGUAGE_CODE,
  MODULE_PERMISSIONS,
} from "../../../../helpers/constants";
import {
  formatApiDataToDropDownOption,
  getCountryCodeWithoutCountryPrefix,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import {
  ProgramSearchProgramFormKey,
  searchTypeKey,
} from "../../modelsAndSchemas/models";
import { useProgramSearchData } from "../../ProgramSearch";
import { programSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ProgramMoreFields } from "../programMoreFields/ProgramMoreFields";
import { GenericParticipantMoreFields } from "./genericParticipantMoreFields/GenericParticipantMoreFields";
import { GenericParticipantSearchFields } from "./genericParticipantSearchFields/GenericParticipantSearchFields";
import { IndividualMoreFields } from "./individualMoreFields/IndividualMoreFields";
import { IndividualsSearchFields } from "./individualsSearchFields/IndividualsSearchFields";
import { NISSearchFields } from "./nisSearchFields/NISSearchFields";
import { NISSearchMoreFields } from "./nisSearchMoreFields/NISSearchMoreFields";
import { ProgramSearchFormWrapper } from "./ProgramSearchForm.style";
import { ProgramsSearchFields } from "./programsSearchFields/ProgramsSearchFields";

interface ProgramTabProps extends FieldValues {
  pageId: string;
  onIndividualProgramRadioToggle: () => void;
  handleSaveFilter: () => void;
}

export const ProgramSearchForm: FC<ProgramTabProps> = ({
  pageId,
  onIndividualProgramRadioToggle,
  handleSaveFilter,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const { setValue, getValues, control, watch, handleSubmit, resetField } =
    useFormContext();

  const [resetForm, setResetForm] = useState(false);
  const { commonLabels, fetchCommonLabel, fetchDropdownValues, fetchLabel } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const currentSearchType = getValues(ProgramSearchProgramFormKey.searchType);

  const getLabelFromSearchForm = (key: string) => {
    return fetchLabel(programSearchSiteCoreKeys.searchFormKey, key);
  };

  const filterDropdownValue = fetchDropdownValues(
    programSearchSiteCoreKeys.searchFormKey,
    programSearchSiteCoreKeys.searchFilterByList,
  );

  const [
    dedicatedProjectManagerMappedData,
    setDedicatedProjectManagerMappedData,
  ] = useState<OptionsDataType[]>([]);
  const [
    clientRelationshipOwnerMappedData,
    setClientRelationshipOwnerMappedData,
  ] = useState<OptionsDataType[]>([]);
  const {
    dedicatedProjectManagerData,
    isDedicatedProjectManagerApiSuccess,
    lineOfBusinessData,
    solutionCategories,
    solutionOfferingsData,
    clientRelationshipOwnerData,
    isClientRelationshipOwnerApiSuccess,
    isValidForm,
    handleSearchClick,
    showMore,
    setShowMore,
    countryListData,
    requestedByListData: requestedByList,
  } = useProgramSearchData(); // Access the data from context

  const {
    data: nextProgramClasses,
    isLoading: classPending,
    refetchWithParams: refetchProgramClass,
    isError: isProgramClassError,
  } = useGetProgramClass();

  const {
    refetchWithParams: refetchDeliveryOffice,
    data: deliveryOfficeListData,
    isLoading: deliveryOfficeListPending,
    isError: isDeliveryOfficeListError,
  } = useGetDeliveryOfficeListQuery();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: deliveryOfficeListPending || classPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [deliveryOfficeListPending, classPending]);

  useEffect(() => {
    if (isDeliveryOfficeListError || isProgramClassError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isDeliveryOfficeListError, isProgramClassError]);

  useEffect(() => {
    if (
      isDedicatedProjectManagerApiSuccess &&
      isNotEmpty(dedicatedProjectManagerData)
    ) {
      const mappedData = formatApiDataToDropDownOption(
        dedicatedProjectManagerData,
      );
      if (isNotEmpty(mappedData)) {
        setDedicatedProjectManagerMappedData(mappedData);
      }
    }
  }, [isDedicatedProjectManagerApiSuccess, dedicatedProjectManagerData]);

  useEffect(() => {
    if (
      isClientRelationshipOwnerApiSuccess &&
      isNotEmpty(clientRelationshipOwnerData)
    ) {
      const mappedData = formatApiDataToDropDownOption(
        clientRelationshipOwnerData,
      );
      if (isNotEmpty(mappedData)) {
        setClientRelationshipOwnerMappedData(mappedData);
      }
    }
  }, [isClientRelationshipOwnerApiSuccess, clientRelationshipOwnerData]);

  const toggleShowMore = () => setShowMore(!showMore);

  const radioOptions = [
    // {
    //   value: searchTypeKey.all,
    //   label: getLabelFromSearchForm(programSearchSiteCoreKeys.allRadioButton),
    // },
    ...(hasModulePermission(MODULE_PERMISSIONS.NIS_VIEW)
      ? [
          {
            value: searchTypeKey.nisParticipants,
            label: getLabelFromSearchForm(
              programSearchSiteCoreKeys.nisParticipants,
            ),
          },
        ]
      : []),

    {
      value: searchTypeKey.individuals,
      label: getLabelFromSearchForm(programSearchSiteCoreKeys.individuals),
    },
    {
      value: searchTypeKey.programs,
      label: getLabelFromSearchForm(programSearchSiteCoreKeys.programs),
    },
    ...(hasModulePermission(MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW)
      ? [
          {
            value: searchTypeKey.genericParticipant,
            label: getLabelFromSearchForm(
              programSearchSiteCoreKeys.genericParticipantsRadioButton,
            ),
          },
        ]
      : []),
  ];

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onIndividualProgramRadioToggle();
    const searchType = e.target.value;
    setValue(ProgramSearchProgramFormKey.searchType, searchType);
  };

  useEffect(() => {
    setResetForm(!resetForm);
  }, [watch(ProgramSearchProgramFormKey.resetForm)]);

  const handleCountryChange = (countryId: string) => {
    resetField(ProgramSearchProgramFormKey.deliveryOffice);

    refetchDeliveryOffice(
      countryId
        ? getCountryCodeWithoutCountryPrefix(countryId).toUpperCase()
        : "",
    );
  };

  const handleSolutionOfferingChange = async (solutionOfferingsId: string) => {
    resetField(ProgramSearchProgramFormKey.nextProgramClass);

    refetchProgramClass({
      languageCode: LANGUAGE_CODE.EN_US,
      solutionOfferingId: solutionOfferingsId,
    });
  };

  return (
    <ProgramSearchFormWrapper>
      <form data-testid="program-form">
        <MuiBox>
          <FormController
            control={control}
            name={ProgramSearchProgramFormKey.searchType}
          >
            <MuiRadioGroup
              label={getLabelFromSearchForm(
                programSearchSiteCoreKeys.selectSearchTypeLabel,
              )}
              wrapperClassName="program-search-radio-group"
              value={currentSearchType}
              onChange={handleSearchTypeChange}
              options={radioOptions}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiBox>
        {currentSearchType === searchTypeKey.individuals && (
          <IndividualsSearchFields
            pageId={pageId}
            resetForm={resetForm}
            filterDropdownValue={filterDropdownValue}
          />
        )}
        {currentSearchType === searchTypeKey.nisParticipants && (
          <NISSearchFields
            pageId={pageId}
            resetForm={resetForm}
            filterDropdownValue={filterDropdownValue}
            dedicatedProjectManagerMappedData={
              dedicatedProjectManagerMappedData
            }
            countryListData={countryListData}
          />
        )}

        {currentSearchType === searchTypeKey.programs && (
          <ProgramsSearchFields
            pageId={pageId}
            dedicatedProjectManagerMappedData={
              dedicatedProjectManagerMappedData
            }
            lineOfBusinessData={lineOfBusinessData}
            filterDropdownValue={filterDropdownValue}
          />
        )}

        {currentSearchType === searchTypeKey.genericParticipant && (
          <GenericParticipantSearchFields
            pageId={pageId}
            filterDropdownValue={filterDropdownValue}
            solutionOfferingsData={solutionOfferingsData}
            handleSolutionOfferingChange={handleSolutionOfferingChange}
          />
        )}

        <MuiBox
          className="expand-btn-container"
          onClick={toggleShowMore}
          data-testid={`${pageId}-expand-btn`}
        >
          <MuiBox tabIndex={0} role="button">
            <MuiTypography
              className="expand-text"
              component="label"
              variant="h3"
            >
              {showMore
                ? fetchCommonLabel(programSearchSiteCoreKeys.showLess)
                : fetchCommonLabel(programSearchSiteCoreKeys.showMore)}
            </MuiTypography>

            {showMore ? <CollapseIcon /> : <ExpandIcon />}
          </MuiBox>
        </MuiBox>

        <Collapse in={showMore} timeout="auto" unmountOnExit>
          {currentSearchType === searchTypeKey.individuals && (
            <IndividualMoreFields
              pageId={pageId}
              filterDropdownValue={filterDropdownValue}
              dedicatedProjectManagerMappedData={
                dedicatedProjectManagerMappedData
              }
              clientRelationshipOwnerMappedData={
                clientRelationshipOwnerMappedData
              }
              lineOfBusinessData={lineOfBusinessData}
              solutionCategories={solutionCategories}
            />
          )}
          {currentSearchType === searchTypeKey.programs && (
            <ProgramMoreFields
              pageId={pageId}
              filterDropdownValue={filterDropdownValue}
              clientRelationshipOwnerMappedData={
                clientRelationshipOwnerMappedData
              }
            />
          )}
          {currentSearchType === searchTypeKey.nisParticipants && (
            <NISSearchMoreFields
              pageId={pageId}
              filterDropdownValue={filterDropdownValue}
            />
          )}
          {currentSearchType === searchTypeKey.genericParticipant && (
            <GenericParticipantMoreFields
              pageId={pageId}
              nextProgramClasses={nextProgramClasses}
              resetForm={resetForm}
              countryListData={countryListData}
              handleCountryChange={handleCountryChange}
              deliveryOfficeListData={deliveryOfficeListData ?? []}
              requestedByList={requestedByList ?? []}
            />
          )}
        </Collapse>
        <MuiGrid container className="button-container">
          <MuiButtonSecondary
            onClick={handleSaveFilter}
            id={`${pageId}-saveFilter`}
            data-testid={`${pageId}-saveFilter`}
            disabled={!isValidForm}
          >
            {commonLabels.saveFilter}
          </MuiButtonSecondary>
          <MuiButtonPrimary
            onClick={handleSubmit(handleSearchClick)}
            id={`${pageId}-search`}
            data-testid={`${pageId}-search`}
          >
            {commonLabels.search}
          </MuiButtonPrimary>
        </MuiGrid>
      </form>
    </ProgramSearchFormWrapper>
  );
};
